import { ChangeDetectionStrategy, Component, ElementRef, OnInit, computed, effect, inject, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { WorkflowNodeConfigBase } from './node-config-base';
import {
  CREATE_RECORD_OBJECT_TYPE_OPTIONS, CRM_OBJECT_TYPE_OPTIONS,
  ENROLL_OBJECT_ID_SOURCE_OPTIONS, LIST_TYPE_OPTIONS, SOURCE_TYPE_OPTIONS, SPV_OPERATION_OPTIONS,
  TARGET_TYPE_OPTIONS
} from './config-options';
import { EntitySelectComponent, EntitySelectPage } from '../../../../../../../shared/components/entity-select/entity-select.component';
import { SegmentService } from '../../../../../../segments/services/segment-api.service';
import { Segment } from '../../../../../../segments/models/segment.model';
import {
  DirectoryUser,
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../../../../../shared/services/user-directory-api.service';
import { AuthService } from '../../../../../../auth-pages/services/auth.service';
import { WorkflowApiService } from '../../../../services/workflow-api.service';
import { WorkflowResponse, WorkflowObjectType } from '../../../../models/workflow.model';
import { WorkflowPropertySelectComponent } from './property-select.component';
import { buildPropertyOptions } from '../../../models/workflow-variable-catalog';
import { WorkflowRecordSelect } from '../../../../shared/workflow-record-select.helper';
import { ContactoApiService } from '../../../../../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../../../../../empresas/services/empresa-api.service';
import { DealApiService } from '../../../../../../deals/services/deal-api.service';
import { QuoteApiService } from '../../../../../../quotes/services/quote-api.service';
import { TicketsApiService } from '../../../../../../tickets/services/tickets-api.service';
import { WorkflowVariablePickerComponent } from '../variable-picker/variable-picker.component';
import { Contact } from '../../../../../../contactos/models/contacto.model';
import { CompanyResponse } from '../../../../../../empresas/models/empresa.model';
import { LeadCycleApiService } from '../../../../../../lead-cycles/services/lead-cycle-api.service';
import { LeadStateApiService } from '../../../../../../lead-states/services/lead-state-api.service';
import { DealCycleApiService } from '../../../../../../deal-cycles/services/deal-cycle-api.service';
import {
  WorkflowConditionValueComponent,
  WorkflowConditionValueParts,
  readConditionValueParts
} from './condition-value.component';

/** Comparación vacía de una regla recién añadida (mismas claves que lee el backend). */
const EMPTY_VALUE_PARTS: WorkflowConditionValueParts = { value: '', from: null, to: null, values: null };

interface FieldRow { property: string; value: unknown; }
interface RoutingRule extends WorkflowConditionValueParts { property: string; operator: string; ownerUserId: number | null; }
/** Opción de un catálogo pequeño (ciclo de lead, tipo de lead, etapa de negocio). */
interface CatalogOption { id: number; name: string; }

/**
 * ACTION → CRM (default): mutaciones de registro (RF-07). Subtipos: SET_PROPERTY_VALUE,
 * COPY_PROPERTY_VALUE, CREATE_RECORD, ASSOCIATE_RECORDS, ASSIGN_OWNER_ROUND_ROBIN,
 * ASSIGN_OWNER_ROUTING, ADD_TO_STATIC_LIST, REMOVE_FROM_STATIC_LIST, ENROLL_IN_WORKFLOW,
 * UNENROLL_FROM_WORKFLOW, DELETE_RECORD. Portado 1:1 del panel viejo.
 */
@Component({
  selector: 'app-wf-crm-action-config',
  standalone: true,
  imports: [
    CommonModule,
    SelectValueSyncDirective,
    EntitySelectComponent,
    WorkflowPropertySelectComponent,
    WorkflowVariablePickerComponent,
    WorkflowConditionValueComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (subtype()) {
      @case ('SET_PROPERTY_VALUE') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="targetType" [ngTemplateOutletContext]="{ parent: 'target' }" />
          <div>
            <label [class]="labelClass">Propiedad</label>
            <app-wf-property-select
              [value]="str('property')" [options]="workflowPropertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchConfig({ property: $event })"></app-wf-property-select>
          </div>
          <div>
            <label [class]="labelClass">Operación</label>
            <select [class]="fieldClass" [value]="str('operation', 'SET')" [disabled]="readOnly()"
                    (change)="patchConfig({ operation: inputValue($event) })">
              @for (o of spvOperations; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
            </select>
          </div>
          @if (str('operation', 'SET') === 'COPY_FROM_PROPERTY') {
            <div>
              <label [class]="labelClass">Propiedad de origen</label>
              <app-wf-property-select
                [value]="str('sourceProperty')" [options]="workflowPropertyOptions()" [disabled]="readOnly()"
                placeholder="Elige una propiedad…"
                (valueChange)="patchConfig({ sourceProperty: $event })"></app-wf-property-select>
            </div>
          } @else if (str('operation', 'SET') !== 'CLEAR') {
            <div>
              <label [class]="labelClass">Valor</label>
              <!--
                INC_CRM_020 (9) inc50 — el valor a asignar se adapta a la propiedad elegida
                (desplegable del catálogo, buscador de registro, número o fecha) en vez de pedir
                siempre texto plano. Una propiedad libre sigue siendo un input de texto.
              -->
              <app-wf-condition-value
                [operator]="'EQUALS'" [value]="config()['value']" [property]="str('property')"
                [options]="workflowPropertyOptions()" [disabled]="readOnly()" placeholder="valor a asignar"
                (patch)="patchConfig({ value: $event['value'] })" />
            </div>
          }
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('COPY_PROPERTY_VALUE') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Tipo de origen</label>
            <select [class]="fieldClass" [value]="nestedStr('source', 'sourceType', 'CONTEXT')" [disabled]="readOnly()"
                    (change)="patchNested('source', { sourceType: inputValue($event) })">
              @for (s of sourceTypes; track s.value) { <option [value]="s.value">{{ s.label }}</option> }
            </select>
          </div>
          <div>
            <label [class]="labelClass">Propiedad de origen</label>
            <app-wf-property-select
              [value]="nestedStr('source', 'property')" [options]="copySourcePropertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchNested('source', { property: $event })"></app-wf-property-select>
          </div>
          <ng-container [ngTemplateOutlet]="targetType" [ngTemplateOutletContext]="{ parent: 'target', label: 'Registro de destino' }" />
          <div>
            <label [class]="labelClass">Propiedad de destino</label>
            <app-wf-property-select
              [value]="nestedStr('target', 'property')" [options]="copyTargetPropertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchNested('target', { property: $event })"></app-wf-property-select>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('CREATE_RECORD') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Tipo de objeto</label>
            <select [class]="fieldClass" [value]="createObjectType()" [disabled]="readOnly()"
                    (change)="onCreateObjectTypeChange(inputValue($event))">
              @for (o of createObjectTypes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
            </select>
          </div>

          @if (createObjectType() === 'LEAD') {
            <div>
              <div class="mb-1.5 flex items-center justify-between">
                <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Nombre del lead</label>
                <app-wf-variable-picker [target]="leadNameField()?.nativeElement ?? null" [disabled]="readOnly()"
                                        (valueChange)="setField('name', $event)" />
              </div>
              <input #leadNameInput type="text" [class]="fieldClass" [value]="fieldStr('name')" [disabled]="readOnly()"
                     [placeholder]="leadNamePlaceholder" (input)="setField('name', inputValue($event))" />
              <p [class]="hintClass">Obligatorio. Admite tokens del registro inscrito con el botón de variables.</p>
            </div>

            <div>
              <label [class]="labelClass">Contacto</label>
              @if (leadUsesEnrolledContact()) {
                <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
                  Se usará el contacto inscrito en el workflow.
                </p>
              } @else {
                <app-entity-select
                  [value]="fieldNum('idContact')" [displayText]="leadContactName()"
                  [searchFn]="contactSearch" [labelFn]="contactLabel" [sublabelFn]="contactSublabel" [disabled]="readOnly()"
                  placeholder="Buscar contacto por nombre o correo…"
                  (valueChange)="setField('idContact', $event)" (selected)="onLeadContactSelected($event)"></app-entity-select>
                <p [class]="hintClass">Obligatorio: el lead siempre pertenece a un contacto.</p>
              }
            </div>

            <div>
              <label [class]="labelClass">Empresa (opcional)</label>
              <app-entity-select
                [value]="fieldNum('idCompany')" [displayText]="leadCompanyName()"
                [searchFn]="companySearch" [labelFn]="companyLabel" [sublabelFn]="companySublabel" [disabled]="readOnly()"
                placeholder="Buscar empresa por nombre…"
                (valueChange)="setField('idCompany', $event)" (selected)="onLeadCompanySelected($event)"></app-entity-select>
            </div>

            <div>
              <label [class]="labelClass">Ciclo del lead (etapa del pipeline)</label>
              <select [class]="fieldClass" [value]="fieldStr('idLeadCycle')" [disabled]="readOnly()"
                      (change)="setField('idLeadCycle', numberOrNull(inputValue($event)))">
                <option value="">(sin definir)</option>
                @for (c of leadCycles(); track c.id) { <option [value]="c.id">{{ c.name }}</option> }
                @if (missingOption(fieldStr('idLeadCycle'), leadCycles(), leadCyclesLoaded())) {
                  <option [value]="fieldStr('idLeadCycle')">{{ fieldStr('idLeadCycle') }} (no está en el catálogo)</option>
                }
              </select>
            </div>

            <div>
              <label [class]="labelClass">Tipo de Lead</label>
              <select [class]="fieldClass" [value]="fieldStr('idLeadState')" [disabled]="readOnly()"
                      (change)="setField('idLeadState', numberOrNull(inputValue($event)))">
                <option value="">(sin definir)</option>
                @for (s of leadStates(); track s.id) { <option [value]="s.id">{{ s.name }}</option> }
                @if (missingOption(fieldStr('idLeadState'), leadStates(), leadStatesLoaded())) {
                  <option [value]="fieldStr('idLeadState')">{{ fieldStr('idLeadState') }} (no está en el catálogo)</option>
                }
              </select>
            </div>

            <div>
              <label [class]="labelClass">Responsable (opcional)</label>
              <select [class]="fieldClass" [value]="fieldStr('userPropietyId')" [disabled]="readOnly()"
                      (change)="setField('userPropietyId', numberOrNull(inputValue($event)))">
                <option value="">(sin asignar)</option>
                @for (u of allUsers(); track u.id) { <option [value]="u.id">{{ userLabel(u) }}</option> }
              </select>
            </div>
          } @else {
            @if (createObjectType() === 'DEAL') {
              <div>
                <label [class]="labelClass">Etapa del pipeline</label>
                <select [class]="fieldClass" [value]="fieldStr('idDealCycle')" [disabled]="readOnly()"
                        (change)="setField('idDealCycle', numberOrNull(inputValue($event)))">
                  <option value="">Elige una etapa…</option>
                  @for (c of dealCycles(); track c.id) { <option [value]="c.id">{{ c.name }}</option> }
                  @if (missingOption(fieldStr('idDealCycle'), dealCycles(), dealCyclesLoaded())) {
                    <option [value]="fieldStr('idDealCycle')">{{ fieldStr('idDealCycle') }} (no está en el catálogo)</option>
                  }
                </select>
                <p [class]="hintClass">Obligatorio: el negocio no se crea sin etapa del pipeline.</p>
              </div>
            }
            <div class="space-y-2">
              <div class="flex items-center justify-between">
                <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Campos</label>
                <button type="button" class="text-xs font-medium text-brand-500 hover:text-brand-600 disabled:opacity-40"
                        [disabled]="readOnly()" (click)="addField()">+ Añadir</button>
              </div>
              @for (row of fieldRows(); track $index) {
                <div class="flex items-center gap-2">
                  <div class="flex-1 min-w-0">
                    <app-wf-property-select
                      [value]="row.property" [options]="createRecordPropertyOptions()" [disabled]="readOnly()"
                      placeholder="propiedad"
                      (valueChange)="updateField($index, { property: $event })"></app-wf-property-select>
                  </div>
                  <!--
                    El valor de cada campo se elige igual que en las condiciones: desplegable del
                    catálogo, buscador de registro, número, fecha o Sí/No según la propiedad, con
                    la opción de usar una variable. Antes había que teclear el valor exacto —el id
                    de un ciclo, el nombre de una etapa— sabiéndoselo de memoria.
                  -->
                  <div class="flex-1 min-w-0">
                    <app-wf-condition-value
                      [operator]="'EQUALS'" [value]="row.value" [property]="row.property"
                      [options]="createRecordPropertyOptions()" [disabled]="readOnly()" placeholder="valor"
                      (patch)="updateField($index, { value: $event['value'] })" />
                  </div>
                  <button type="button" class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:text-error-500 disabled:opacity-40"
                          [disabled]="readOnly()" (click)="removeField($index)"><i class="fa-solid fa-xmark text-xs"></i></button>
                </div>
              }
            </div>
          }

          <label class="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
            <input type="checkbox" class="rounded border-gray-300" [checked]="bool('associateWithCurrentRecord', true)" [disabled]="readOnly()"
                   (change)="patchConfig({ associateWithCurrentRecord: checkboxValue($event) })" />
            Asociar al registro actual
          </label>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('ASSOCIATE_RECORDS') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="targetType" [ngTemplateOutletContext]="{ parent: 'source', label: 'Registro de origen' }" />
          <div>
            <label [class]="labelClass">Objeto destino</label>
            <select [class]="fieldClass" [value]="nestedStr('target', 'objectType', 'COMPANY')" [disabled]="readOnly()"
                    (change)="onAssociateObjectTypeChange(inputValue($event))">
              @for (o of crmObjectTypes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
            </select>
          </div>
          <div>
            <label [class]="labelClass">Registro destino</label>
            @if (associateSearchable()) {
              <app-entity-select
                [value]="nestedNum('target', 'objectId') || null" [displayText]="associateTargetName()"
                [searchFn]="associateSelect.search" [labelFn]="associateSelect.label" [sublabelFn]="associateSelect.sublabel" [disabled]="readOnly()"
                placeholder="Buscar por nombre…"
                (valueChange)="patchNested('target', { objectId: $event })" (selected)="onAssociateTargetSelected($event)"></app-entity-select>
            } @else {
              <input type="number" [class]="fieldClass" [value]="nestedStr('target', 'objectId')" [disabled]="readOnly()"
                     (change)="patchNested('target', { objectId: numberOrNull(inputValue($event)) })" />
            }
          </div>
          <!--
            Antes esto era un texto libre cuyo único efecto era acertar con una de dos claves
            mágicas (PRIMARY_COMPANY / PRIMARY_CONTACT); cualquier otra cosa creaba la asociación
            como secundaria sin decirlo. Ahora se elige el CONCEPTO y el componente escribe la
            clave que toca según el par origen→destino, que es de quien depende.
          -->
          @if (associationPrimaryKey(); as primaryKey) {
            <div>
              <label [class]="labelClass">Tipo de asociación</label>
              <select [class]="fieldClass" [value]="isPrimaryAssociation() ? 'PRIMARY' : 'SECONDARY'" [disabled]="readOnly()"
                      (change)="patchConfig({ associationType: inputValue($event) === 'PRIMARY' ? primaryKey : 'SECONDARY' })">
                <option value="PRIMARY">Principal</option>
                <option value="SECONDARY">Secundaria</option>
              </select>
              <p [class]="hintClass">
                La principal es la que se muestra como asociación por defecto en la ficha del registro.
              </p>
            </div>
          }
          @if (associationUnsupported()) {
            <p class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
              <i class="fa-solid fa-triangle-exclamation mr-1"></i>
              Esta combinación no está soportada. Se pueden asociar: contacto → empresa, negocio → contacto,
              negocio → empresa y cotización → negocio.
            </p>
          }
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('ASSIGN_OWNER_ROUND_ROBIN') {
        <div class="space-y-4">
          <ng-container [ngTemplateOutlet]="targetType" [ngTemplateOutletContext]="{ parent: 'target' }" />
          <div>
            <label [class]="labelClass">Usuarios del reparto</label>
            <div class="mb-2 flex flex-wrap gap-1.5">
              @for (id of roundRobinIds(); track id) {
                <span class="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
                  {{ userNameById(id) }}
                  <button type="button" class="text-brand-400 hover:text-error-500 disabled:opacity-40" [disabled]="readOnly()"
                          (click)="removeRoundRobin(id)"><i class="fa-solid fa-xmark text-[10px]"></i></button>
                </span>
              }
            </div>
            <select [class]="fieldClass" value="" [disabled]="readOnly()" (change)="addRoundRobin(inputValue($event))">
              <option value="">Añadir usuario…</option>
              @for (u of allUsers(); track u.id) { <option [value]="u.id">{{ userLabel(u) }}</option> }
            </select>
          </div>
          <div>
            <label [class]="labelClass">Estrategia</label>
            <select [class]="fieldClass" [value]="str('strategy', 'SEQUENTIAL')" [disabled]="readOnly()"
                    (change)="patchConfig({ strategy: inputValue($event) })">
              <option value="SEQUENTIAL">Secuencial</option>
              <option value="RANDOM">Aleatoria</option>
            </select>
          </div>
          <div>
            <label [class]="labelClass">Clave de estado (opcional)</label>
            <input type="text" [class]="fieldClass" [value]="str('stateKey')" [disabled]="readOnly()"
                   (input)="patchConfig({ stateKey: inputValue($event) })" />
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('ASSIGN_OWNER_ROUTING') {
        <div class="space-y-3">
          <ng-container [ngTemplateOutlet]="targetType" [ngTemplateOutletContext]="{ parent: 'target' }" />
          <label class="text-xs font-medium text-gray-700 dark:text-gray-300">Reglas de enrutamiento</label>
          @for (row of routingRules(); track $index) {
            <div class="space-y-2 rounded-lg border border-gray-200 p-2.5 dark:border-gray-800">
              <div class="flex items-center gap-2">
                <div class="flex-1 min-w-0">
                  <app-wf-property-select
                    [value]="row.property" [options]="workflowPropertyOptions()" [disabled]="readOnly()"
                    placeholder="propiedad"
                    (valueChange)="updateRule($index, { property: $event })"></app-wf-property-select>
                </div>
                <button type="button" class="flex h-8 w-8 shrink-0 items-center justify-center rounded text-gray-400 hover:text-error-500 disabled:opacity-40"
                        [disabled]="readOnly()" (click)="removeRule($index)"><i class="fa-solid fa-xmark text-xs"></i></button>
              </div>
              <select class="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                      [value]="row.operator" [disabled]="readOnly()" (change)="updateRule($index, { operator: inputValue($event) })">
                @for (o of operatorOptionsFor(workflowPropertyOptions(), row.property, row.operator); track o.value) {
                  <option [value]="o.value">{{ o.label }}</option>
                }
              </select>
              <app-wf-condition-value
                [operator]="row.operator" [value]="row.value" [from]="row.from" [to]="row.to" [values]="row.values"
                [property]="row.property" [options]="workflowPropertyOptions()"
                [disabled]="readOnly()" (patch)="updateRule($index, $event)" />
              <select [class]="fieldClass" [value]="row.ownerUserId ?? ''" [disabled]="readOnly()"
                      (change)="updateRule($index, { ownerUserId: numberOrNull(inputValue($event)) })">
                <option value="">Responsable…</option>
                @for (u of allUsers(); track u.id) { <option [value]="u.id">{{ userLabel(u) }}</option> }
              </select>
            </div>
          }
          <button type="button" class="text-xs font-medium text-brand-500 hover:text-brand-600 disabled:opacity-40"
                  [disabled]="readOnly()" (click)="addRule()">+ Añadir regla</button>
          <div>
            <label [class]="labelClass">Responsable por defecto</label>
            <select [class]="fieldClass" [value]="str('fallbackOwnerUserId')" [disabled]="readOnly()"
                    (change)="patchConfig({ fallbackOwnerUserId: numberOrNull(inputValue($event)) })">
              <option value="">(ninguno)</option>
              @for (u of allUsers(); track u.id) { <option [value]="u.id">{{ userLabel(u) }}</option> }
            </select>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('ADD_TO_STATIC_LIST') { <ng-container [ngTemplateOutlet]="staticList" /> }
      @case ('REMOVE_FROM_STATIC_LIST') { <ng-container [ngTemplateOutlet]="staticList" /> }

      @case ('ENROLL_IN_WORKFLOW') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Workflow destino</label>
            <app-entity-select
              [value]="num('targetWorkflowId') || null" [displayText]="workflowName()"
              [searchFn]="workflowSearch" [labelFn]="workflowLabel" [disabled]="readOnly()"
              placeholder="Buscar workflow…"
              (valueChange)="onEnrollWorkflowId($event)" (selected)="onEnrollWorkflowSelected($event)"></app-entity-select>
          </div>
          <div>
            <label [class]="labelClass">Tipo de objeto</label>
            <select [class]="fieldClass" [value]="str('objectType', 'CONTACT')" [disabled]="readOnly()"
                    (change)="onEnrollObjectTypeChange(inputValue($event))">
              @for (o of crmObjectTypes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
            </select>
          </div>
          <div>
            <label [class]="labelClass">Origen del id</label>
            <select [class]="fieldClass" [value]="str('objectIdSource', 'CURRENT_RECORD')" [disabled]="readOnly()"
                    (change)="patchConfig({ objectIdSource: inputValue($event) })">
              @for (s of objectIdSources; track s.value) { <option [value]="s.value">{{ s.label }}</option> }
            </select>
          </div>
          @if (str('objectIdSource', 'CURRENT_RECORD') === 'EXPLICIT') {
            <div>
              <label [class]="labelClass">Registro</label>
              @if (enrollSearchable()) {
                <app-entity-select
                  [value]="num('objectId') || null" [displayText]="enrollObjectName()"
                  [searchFn]="enrollRecordSelect.search" [labelFn]="enrollRecordSelect.label" [sublabelFn]="enrollRecordSelect.sublabel" [disabled]="readOnly()"
                  placeholder="Buscar por nombre…"
                  (valueChange)="patchConfig({ objectId: $event })" (selected)="onEnrollObjectSelected($event)"></app-entity-select>
              } @else {
                <input type="number" [class]="fieldClass" [value]="str('objectId')" [disabled]="readOnly()"
                       (change)="patchConfig({ objectId: numberOrNull(inputValue($event)) })" />
              }
            </div>
          }
          <div>
            <label [class]="labelClass">Contexto (JSON)</label>
            <textarea rows="3" [class]="jsonAreaClass" [value]="jsonOf('context')" [disabled]="readOnly()"
                      (change)="setJson('context', inputValue($event))"></textarea>
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('UNENROLL_FROM_WORKFLOW') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Workflow destino</label>
            <app-entity-select
              [value]="num('targetWorkflowId') || null" [displayText]="workflowName()"
              [searchFn]="workflowSearch" [labelFn]="workflowLabel" [disabled]="readOnly()"
              placeholder="Buscar workflow…"
              (valueChange)="patchConfig({ targetWorkflowId: $event })" (selected)="onUnenrollWorkflowSelected($event)"></app-entity-select>
          </div>
          <div>
            <label [class]="labelClass">Tipo de objeto</label>
            <select [class]="fieldClass" [value]="str('objectType', 'CONTACT')" [disabled]="readOnly()"
                    (change)="onEnrollObjectTypeChange(inputValue($event))">
              @for (o of crmObjectTypes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
            </select>
          </div>
          <div>
            <label [class]="labelClass">Origen del id</label>
            <select [class]="fieldClass" [value]="str('objectIdSource', 'CURRENT_RECORD')" [disabled]="readOnly()"
                    (change)="patchConfig({ objectIdSource: inputValue($event) })">
              @for (s of objectIdSources; track s.value) { <option [value]="s.value">{{ s.label }}</option> }
            </select>
          </div>
          @if (str('objectIdSource', 'CURRENT_RECORD') === 'EXPLICIT') {
            <div>
              <label [class]="labelClass">Registro</label>
              @if (enrollSearchable()) {
                <app-entity-select
                  [value]="num('objectId') || null" [displayText]="enrollObjectName()"
                  [searchFn]="enrollRecordSelect.search" [labelFn]="enrollRecordSelect.label" [sublabelFn]="enrollRecordSelect.sublabel" [disabled]="readOnly()"
                  placeholder="Buscar por nombre…"
                  (valueChange)="patchConfig({ objectId: $event })" (selected)="onEnrollObjectSelected($event)"></app-entity-select>
              } @else {
                <input type="number" [class]="fieldClass" [value]="str('objectId')" [disabled]="readOnly()"
                       (change)="patchConfig({ objectId: numberOrNull(inputValue($event)) })" />
              }
            </div>
          }
          <div>
            <label [class]="labelClass">Motivo (opcional)</label>
            <input type="text" [class]="fieldClass" [value]="str('reason')" [disabled]="readOnly()"
                   (input)="patchConfig({ reason: inputValue($event) })" />
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @case ('DELETE_RECORD') {
        <div class="space-y-4">
          <!--
            Sin selector de registro objetivo A PROPÓSITO: el ejecutor borra SIEMPRE el registro
            inscrito (executeDeleteRecord trabaja sobre enrollment.getObjectId() y no mira el
            target). Ofrecer "un registro concreto que yo elija" en una acción irreversible era
            lo más peligroso del inspector: el usuario creía apuntar a otro registro.
          -->
          <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
            Se elimina el registro inscrito en el workflow. Esta acción no permite apuntar a otro registro.
          </p>
          @if (!dangerousActionsAllowed()) {
            <p class="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] leading-relaxed text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400">
              <i class="fa-solid fa-triangle-exclamation mr-1"></i>
              Este workflow no permite acciones peligrosas, así que el paso fallará al ejecutarse. Actívalo en los
              ajustes del workflow (Editar workflow → «Permitir acciones peligrosas»).
            </p>
          }
          <div class="rounded-lg border border-error-200 bg-error-50 p-3 dark:border-error-500/30 dark:bg-error-500/10">
            <label class="flex items-center gap-2 text-xs font-medium text-error-600 dark:text-error-400">
              <input type="checkbox" class="rounded border-gray-300" [checked]="bool('confirmDelete')" [disabled]="readOnly()"
                     (change)="patchConfig({ confirmDelete: checkboxValue($event) })" />
              Confirmo que quiero eliminar el registro (irreversible)
            </label>
          </div>
          <div>
            <label [class]="labelClass">Motivo (obligatorio)</label>
            <input type="text" [class]="fieldClass" [value]="str('reason')" [disabled]="readOnly()"
                   (input)="patchConfig({ reason: inputValue($event) })" />
          </div>
          <ng-container [ngTemplateOutlet]="continueOnError" />
        </div>
      }

      @default {
        <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
          Esta acción no tiene un formulario dedicado. Usa el modo avanzado (JSON) para configurarla.
        </p>
      }
    }

    <!--
      El registro sobre el que actúa la acción. Elegir "un registro concreto" TIENE que dejar
      elegirlo: el backend resuelve el destino con target.objectType + target.objectId
      (WorkflowCrmActionService.resolveTargetRecord) y, sin ellos, la acción muere en ejecución
      con "No se pudo resolver el registro destino de la acción". Antes solo se guardaba el tipo,
      así que la opción existía pero era imposible de completar salvo por el JSON crudo.
    -->
    <ng-template #targetType let-parent="parent" let-label="label">
      <div class="space-y-3">
        <div>
          <label [class]="labelClass">{{ label || 'Registro objetivo' }}</label>
          <select [class]="fieldClass" [value]="nestedStr(parent, 'type', 'CURRENT_RECORD')" [disabled]="readOnly()"
                  (change)="onTargetTypeChange(parent, inputValue($event))">
            @for (t of targetTypes; track t.value) { <option [value]="t.value">{{ t.label }}</option> }
          </select>
        </div>
        @if (nestedStr(parent, 'type', 'CURRENT_RECORD') !== 'CURRENT_RECORD') {
          <div>
            <label [class]="labelClass">Tipo de registro</label>
            <select [class]="fieldClass" [value]="targetObjectType(parent)" [disabled]="readOnly()"
                    (change)="onTargetObjectTypeChange(parent, inputValue($event))">
              @for (o of crmObjectTypes; track o.value) { <option [value]="o.value">{{ o.label }}</option> }
            </select>
          </div>
          <div>
            <label [class]="labelClass">Registro</label>
            <app-entity-select
              [value]="nestedNum(parent, 'objectId') || null" [displayText]="targetRecordName(parent)"
              [searchFn]="recordSelect(parent).search" [labelFn]="recordSelect(parent).label"
              [sublabelFn]="recordSelect(parent).sublabel" [disabled]="readOnly()"
              placeholder="Buscar por nombre…"
              (valueChange)="patchNested(parent, { objectId: $event })"
              (selected)="onTargetRecordSelected(parent, $event)"></app-entity-select>
            <p [class]="hintClass">
              Obligatorio: sin un registro elegido, la acción falla con «No se pudo resolver el registro destino».
            </p>
          </div>
        }
      </div>
    </ng-template>

    <ng-template #staticList>
      <div class="space-y-4">
        <div>
          <label [class]="labelClass">Tipo de lista</label>
          <select [class]="fieldClass" [value]="str('listType', 'CONTACT_SEGMENT')" [disabled]="readOnly()"
                  (change)="patchConfig({ listType: inputValue($event) })">
            @for (t of listTypes; track t.value) { <option [value]="t.value">{{ t.label }}</option> }
          </select>
        </div>
        <div>
          <label [class]="labelClass">Lista estática (segmento)</label>
          <app-entity-select
            [value]="num('listId') || null" [displayText]="listName()"
            [searchFn]="listSearch" [labelFn]="segmentLabel" [disabled]="readOnly()"
            placeholder="Buscar lista estática…"
            (valueChange)="patchConfig({ listId: $event })" (selected)="onListSelected($event)"></app-entity-select>
        </div>
        <ng-container [ngTemplateOutlet]="continueOnError" />
      </div>
    </ng-template>

    <ng-template #continueOnError>
      <label class="flex items-center gap-2 border-t border-gray-100 pt-3 text-xs text-gray-700 dark:border-gray-800 dark:text-gray-300">
        <input type="checkbox" class="rounded border-gray-300" [checked]="bool('continueOnError')" [disabled]="readOnly()"
               (change)="patchConfig({ continueOnError: checkboxValue($event) })" />
        Continuar el flujo si esta acción falla
      </label>
    </ng-template>
  `
})
export class WorkflowCrmActionConfigComponent extends WorkflowNodeConfigBase implements OnInit {
  private readonly segmentService = inject(SegmentService);
  private readonly userDirectory = inject(UserDirectoryApiService);
  private readonly auth = inject(AuthService);
  private readonly workflowApi = inject(WorkflowApiService);
  private readonly contactoApi = inject(ContactoApiService);
  private readonly empresaApi = inject(EmpresaApiService);
  private readonly dealApi = inject(DealApiService);
  private readonly quoteApi = inject(QuoteApiService);
  private readonly ticketsApi = inject(TicketsApiService);
  private readonly leadCycleApi = inject(LeadCycleApiService);
  private readonly leadStateApi = inject(LeadStateApiService);
  private readonly dealCycleApi = inject(DealCycleApiService);

  /** ASSOCIATE_RECORDS: buscar el registro destino por nombre según target.objectType. */
  protected associateSelect!: WorkflowRecordSelect;
  protected readonly associateTargetName = signal('');
  /** ENROLL/UNENROLL (objectIdSource=EXPLICIT): buscar el registro por nombre según objectType. */
  protected enrollRecordSelect!: WorkflowRecordSelect;
  protected readonly enrollObjectName = signal('');
  /** Buscadores del registro objetivo, uno por rama de la config (`target` y `source`). */
  private targetSelects: Record<string, WorkflowRecordSelect> = {};
  protected readonly targetRecordNames = signal<Record<string, string>>({});

  private isSearchableType(t: string): boolean {
    return t === 'CONTACT' || t === 'COMPANY' || t === 'DEAL' || t === 'QUOTE';
  }
  protected readonly associateSearchable = computed(() => this.isSearchableType(this.nestedStr('target', 'objectType', 'COMPANY')));
  protected readonly enrollSearchable = computed(() => this.isSearchableType(this.str('objectType', 'CONTACT')));

  protected readonly spvOperations = SPV_OPERATION_OPTIONS;
  protected readonly targetTypes = TARGET_TYPE_OPTIONS;
  protected readonly sourceTypes = SOURCE_TYPE_OPTIONS;
  protected readonly crmObjectTypes = CRM_OBJECT_TYPE_OPTIONS;
  protected readonly createObjectTypes = CREATE_RECORD_OBJECT_TYPE_OPTIONS;
  protected readonly listTypes = LIST_TYPE_OPTIONS;
  protected readonly objectIdSources = ENROLL_OBJECT_ID_SOURCE_OPTIONS;
  protected readonly subtype = computed(() => this.node().nodeSubtype ?? '');

  /** Propiedades sugeridas del objeto base del workflow (etiqueta legible + clave). */
  protected readonly workflowPropertyOptions = computed(() => buildPropertyOptions(this.editor.workflow()?.objectType ?? null));

  /**
   * DELETE_RECORD exige `settings.allowDangerousActions` en el WORKFLOW, no en el nodo. Sin él la
   * acción falla en ejecución con un mensaje que nombra una clave de JSON, así que el panel avisa
   * y remite al ajuste que ahora existe en el formulario del workflow.
   */
  protected readonly dangerousActionsAllowed = computed(
    () => this.editor.workflow()?.settings?.['allowDangerousActions'] === true
  );

  /** CREATE_RECORD: propiedades del objeto elegido en el propio nodo; si no mapea, las del workflow. */
  protected readonly createRecordPropertyOptions = computed(() => {
    const opts = buildPropertyOptions(this.str('objectType', 'DEAL') as WorkflowObjectType);
    return opts.length ? opts : this.workflowPropertyOptions();
  });

  /** COPY: propiedades del objeto origen si su objectType está definido; si no, las del workflow. */
  protected readonly copySourcePropertyOptions = computed(() => {
    const objectType = this.nestedStr('source', 'objectType');
    const opts = objectType ? buildPropertyOptions(objectType as WorkflowObjectType) : [];
    return opts.length ? opts : this.workflowPropertyOptions();
  });

  /** COPY: propiedades del objeto destino si su objectType está definido; si no, las del workflow. */
  protected readonly copyTargetPropertyOptions = computed(() => {
    const objectType = this.nestedStr('target', 'objectType');
    const opts = objectType ? buildPropertyOptions(objectType as WorkflowObjectType) : [];
    return opts.length ? opts : this.workflowPropertyOptions();
  });
  protected readonly jsonAreaClass =
    'w-full rounded-lg border border-gray-300 px-2 py-1.5 font-mono text-[11px] text-gray-800 focus:border-brand-500 focus:outline-none disabled:opacity-60 dark:border-gray-700 dark:bg-gray-950 dark:text-white/90';

  protected readonly allUsers = signal<DirectoryUser[]>([]);
  protected readonly segmentNameCache = signal('');
  protected readonly listName = signal('');
  protected readonly workflowName = signal('');

  constructor() {
    super();
    effect(() => {
      const id = this.num('listId');
      if (!id) {
        this.listName.set('');
        return;
      }
      this.segmentService.getSegmentById(id).subscribe({ next: (s) => this.listName.set(s.name), error: () => {} });
    });
    effect(() => {
      const id = this.num('targetWorkflowId');
      if (!id) {
        this.workflowName.set('');
        return;
      }
      this.workflowApi.getById(id).subscribe({ next: (w) => this.workflowName.set(w.name), error: () => {} });
    });
    // Catálogos de CREATE_RECORD: solo se piden para el tipo de registro elegido.
    effect(() => {
      if (this.subtype() !== 'CREATE_RECORD') {
        return;
      }
      // El panel PINTA "Negocio" por defecto; sin persistirlo el backend rechaza el nodo con
      // "CREATE_RECORD requiere objectType y fields". Se persiste lo que se ve.
      if (this.config()['objectType'] == null) {
        this.patchConfig({ objectType: 'DEAL' });
      }
      const objectType = this.createObjectType();
      if (objectType === 'LEAD') {
        this.loadLeadCatalogs();
      } else if (objectType === 'DEAL') {
        this.loadDealCycles();
      }
    });
    // `CONTEXT` desapareció de las opciones de registro objetivo porque nunca leyó el contexto:
    // en `resolveTargetRecord` cae al mismo `default` que EXPLICIT_RECORD (objectType + objectId).
    // Los nodos que ya lo tuvieran guardado se normalizan para que el desplegable no mienta.
    effect(() => {
      if (this.readOnly()) {
        return;
      }
      for (const parent of ['target', 'source']) {
        if (this.nestedStr(parent, 'type') === 'CONTEXT') {
          this.patchNested(parent, { type: 'EXPLICIT_RECORD' });
        }
      }
    });
    // Nombres a mostrar en los buscadores del lead al reabrir un nodo ya guardado.
    effect(() => {
      const id = this.createObjectType() === 'LEAD' ? this.fieldNum('idContact') : null;
      if (!id) {
        this.leadContactName.set('');
        return;
      }
      this.contactoApi.getContactById(id).subscribe({
        next: (c) => this.leadContactName.set(this.contactLabel(c)),
        error: () => {}
      });
    });
    effect(() => {
      const id = this.createObjectType() === 'LEAD' ? this.fieldNum('idCompany') : null;
      if (!id) {
        this.leadCompanyName.set('');
        return;
      }
      this.empresaApi.getCompanyById(id).subscribe({
        next: (c) => this.leadCompanyName.set(this.companyLabel(c)),
        error: () => {}
      });
    });
  }

  ngOnInit(): void {
    const businessId = () => this.auth.getCurrentUser()?.businessId || 1;
    this.associateSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.nestedStr('target', 'objectType', 'COMPANY'), businessId
    );
    this.enrollRecordSelect = new WorkflowRecordSelect(
      this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
      () => this.str('objectType', 'CONTACT'), businessId
    );
    this.targetSelects = {
      target: new WorkflowRecordSelect(
        this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
        () => this.targetObjectType('target'), businessId
      ),
      source: new WorkflowRecordSelect(
        this.contactoApi, this.empresaApi, this.dealApi, this.quoteApi, this.ticketsApi,
        () => this.targetObjectType('source'), businessId
      )
    };
    // Directorio de la empresa (negocio desde el token, sin exigir `users:VIEW`): con
    // /api/auth/users/search los selects de «asignar responsable» quedaban vacíos para
    // cualquier rol que no fuera Super Admin, Admin o Manager.
    this.userDirectory.searchUsers('', 200).subscribe({
      next: (users) => this.allUsers.set(users),
      error: () => {}
    });
  }

  // ── ASSOCIATE_RECORDS ────────────────────────────────────────────────────────

  /** Objeto de ORIGEN de la asociación: el del nodo si es explícito, si no el del workflow. */
  private readonly associationSourceType = computed(() =>
    this.nestedStr('source', 'type', 'CURRENT_RECORD') === 'CURRENT_RECORD'
      ? (this.editor.workflow()?.objectType ?? '')
      : this.nestedStr('source', 'objectType', this.editor.workflow()?.objectType ?? ''));

  private readonly associationPair = computed(
    () => `${this.associationSourceType()}->${this.nestedStr('target', 'objectType', 'COMPANY')}`
  );

  /**
   * Clave que marca la asociación como PRINCIPAL para el par actual, o cadena vacía si en ese par
   * no existe tal concepto: negocio→empresa y cotización→negocio son claves foráneas directas y
   * el ejecutor ni mira `associationType`, así que pedirlo solo confundía.
   */
  protected readonly associationPrimaryKey = computed(() => {
    switch (this.associationPair()) {
      case 'CONTACT->COMPANY':
        return 'PRIMARY_COMPANY';
      case 'DEAL->CONTACT':
        return 'PRIMARY_CONTACT';
      default:
        return '';
    }
  });

  protected readonly isPrimaryAssociation = computed(
    () => this.str('associationType').toUpperCase() === this.associationPrimaryKey()
  );

  /** Pares que el ejecutor NO sabe asociar: se avisa en vez de fallar en ejecución. */
  protected readonly associationUnsupported = computed(() => {
    const supported = ['CONTACT->COMPANY', 'DEAL->CONTACT', 'DEAL->COMPANY', 'QUOTE->DEAL'];
    const pair = this.associationPair();
    // Con el origen sin resolver todavía (workflow aún cargando) no se avisa de nada.
    return this.associationSourceType() !== '' && !supported.includes(pair);
  });

  // ── Registro objetivo (target/source de resolveTargetRecord) ─────────────────

  /**
   * Tipo de registro al que apunta la acción cuando NO es el registro inscrito.
   *
   * El backend cae al objectType de la inscripción cuando la clave falta, así que ese mismo es el
   * valor que se muestra y el que `onTargetTypeChange` persiste: lo que se ve es lo que se guarda.
   */
  protected targetObjectType(parent: string): string {
    return this.nestedStr(parent, 'objectType', this.editor.workflow()?.objectType ?? 'CONTACT');
  }

  protected recordSelect(parent: string): WorkflowRecordSelect {
    return this.targetSelects[parent] ?? this.targetSelects['target'];
  }

  protected targetRecordName(parent: string): string {
    return this.targetRecordNames()[parent] ?? '';
  }

  protected onTargetTypeChange(parent: string, type: string): void {
    if (type === 'CURRENT_RECORD') {
      this.patchNested(parent, { type });
      return;
    }
    this.patchNested(parent, { type, objectType: this.targetObjectType(parent) });
  }

  /** Cambiar de tipo invalida el registro elegido: los ids no son intercambiables entre objetos. */
  protected onTargetObjectTypeChange(parent: string, objectType: string): void {
    this.patchNested(parent, { objectType, objectId: null });
    this.setTargetRecordName(parent, '');
  }

  protected onTargetRecordSelected(parent: string, item: unknown | null): void {
    this.setTargetRecordName(parent, item ? this.recordSelect(parent).label(item) : '');
  }

  private setTargetRecordName(parent: string, name: string): void {
    this.targetRecordNames.update((current) => ({ ...current, [parent]: name }));
  }

  protected onAssociateObjectTypeChange(objectType: string): void {
    this.patchNested('target', { objectType, objectId: null });
    this.associateTargetName.set('');
  }
  protected onAssociateTargetSelected(item: unknown | null): void {
    this.associateTargetName.set(item ? this.associateSelect.label(item) : '');
  }
  protected onEnrollObjectTypeChange(objectType: string): void {
    this.patchConfig({ objectType, objectId: null });
    this.enrollObjectName.set('');
  }
  protected onEnrollObjectSelected(item: unknown | null): void {
    this.enrollObjectName.set(item ? this.enrollRecordSelect.label(item) : '');
  }

  protected numberOrNull(value: string): number | null {
    const n = Number(value);
    return value.trim() && Number.isFinite(n) ? n : null;
  }

  protected userLabel = (u: DirectoryUser): string => directoryUserLabel(u);

  protected userNameById(id: number): string {
    const u = this.allUsers().find((x) => x.id === id);
    // El directorio solo lista ACTIVOS: no encontrarlo aquí significa usuario dado de baja o
    // eliminado, y se dice con palabras en vez de enseñar el id.
    return u ? this.userLabel(u) : 'Usuario no disponible';
  }

  // ── CREATE_RECORD fields ─────────────────────────────────────────────────────

  /** Tipo de registro a crear. LEAD y DEAL tienen controles dedicados; el resto, lista genérica. */
  protected readonly createObjectType = computed(() => this.str('objectType', 'DEAL'));

  /** `config.fields` tal cual está guardado (objeto plano propiedad → valor). */
  protected readonly fieldsObject = computed<Record<string, unknown>>(() => {
    const raw = this.config()['fields'];
    return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  });

  /**
   * Claves que el tipo elegido edita con un control DEDICADO. Se excluyen de la lista genérica
   * para no editarlas dos veces, pero se CONSERVAN al reescribir `fields` (ver `writeFields`).
   */
  private readonly namedFieldKeys = computed<string[]>(() => {
    switch (this.createObjectType()) {
      case 'LEAD':
        return ['name', 'idContact', 'idCompany', 'idLeadCycle', 'idLeadState', 'userPropietyId'];
      case 'DEAL':
        return ['idDealCycle'];
      default:
        return [];
    }
  });

  protected fieldStr(key: string): string {
    const value = this.fieldsObject()[key];
    return value == null ? '' : String(value);
  }

  protected fieldNum(key: string): number | null {
    const value = this.fieldsObject()[key];
    if (value == null || value === '') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  /** Escribe UNA clave de `config.fields` conservando el resto (null/'' la elimina). */
  protected setField(key: string, value: unknown): void {
    const fields = { ...this.fieldsObject() };
    if (value === null || value === undefined || value === '') {
      delete fields[key];
    } else {
      fields[key] = value;
    }
    this.patchConfig({ fields });
  }

  /** Cambiar de tipo invalida los campos del anterior (los ids no son intercambiables). */
  protected onCreateObjectTypeChange(objectType: string): void {
    this.patchConfig({ objectType, fields: {} });
    this.leadContactName.set('');
    this.leadCompanyName.set('');
  }

  /** El valor guardado ya no está en el catálogo (o es un token) → se conserva como opción. */
  protected missingOption(raw: string, options: ReadonlyArray<CatalogOption>, loaded: boolean): boolean {
    return raw !== '' && loaded && !options.some((o) => String(o.id) === raw);
  }

  protected readonly fieldRows = computed<FieldRow[]>(() => {
    const named = this.namedFieldKeys();
    return Object.entries(this.fieldsObject())
      .filter(([property]) => !named.includes(property))
      .map(([property, value]) => ({ property, value: value ?? '' }));
  });
  private writeFields(rows: FieldRow[]): void {
    const current = this.fieldsObject();
    const fields: Record<string, unknown> = {};
    // Primero las claves con control dedicado (no están en `rows`, se perderían).
    for (const key of this.namedFieldKeys()) {
      if (current[key] !== undefined) {
        fields[key] = current[key];
      }
    }
    for (const r of rows) {
      if (r.property.trim()) {
        fields[r.property.trim()] = r.value;
      }
    }
    this.patchConfig({ fields });
  }
  protected addField(): void {
    this.patchConfig({ fields: { ...this.fieldsObject(), '': '' } });
  }
  protected updateField(index: number, patch: Partial<FieldRow>): void {
    this.writeFields(this.fieldRows().map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }
  protected removeField(index: number): void {
    this.writeFields(this.fieldRows().filter((_, i) => i !== index));
  }

  // ── CREATE_RECORD → LEAD ─────────────────────────────────────────────────────

  /** Ejemplo del placeholder: las llaves se construyen en TS (en plantilla serían interpolación). */
  protected readonly leadNamePlaceholder = 'Oportunidad {{contact.fullname}}';

  protected readonly leadNameField = viewChild<ElementRef<HTMLInputElement>>('leadNameInput');

  /**
   * El backend resuelve el contacto del lead con el registro inscrito cuando el workflow es de
   * CONTACTO y se pide asociar al registro actual → no hay nada que elegir.
   */
  protected readonly leadUsesEnrolledContact = computed(
    () => this.bool('associateWithCurrentRecord', true) && this.editor.workflow()?.objectType === 'CONTACT'
  );

  protected readonly leadContactName = signal('');
  protected readonly leadCompanyName = signal('');

  protected readonly leadCycles = signal<CatalogOption[]>([]);
  protected readonly leadStates = signal<CatalogOption[]>([]);
  protected readonly dealCycles = signal<CatalogOption[]>([]);
  protected readonly leadCyclesLoaded = signal(false);
  protected readonly leadStatesLoaded = signal(false);
  protected readonly dealCyclesLoaded = signal(false);
  /** Evita repetir los GET en cada ciclo del effect. */
  private catalogRequested = { leadCycles: false, leadStates: false, dealCycles: false };

  private loadLeadCatalogs(): void {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    if (!this.catalogRequested.leadCycles) {
      this.catalogRequested.leadCycles = true;
      this.leadCycleApi.getCycles(businessId, 0, 200).subscribe({
        next: (list) => {
          this.leadCycles.set((list ?? []).filter((c) => c.isActive !== false).map((c) => ({ id: c.id, name: c.name })));
          this.leadCyclesLoaded.set(true);
        },
        error: () => (this.catalogRequested.leadCycles = false)
      });
    }
    if (!this.catalogRequested.leadStates) {
      this.catalogRequested.leadStates = true;
      this.leadStateApi.getStates(businessId, 0, 200).subscribe({
        next: (list) => {
          this.leadStates.set((list ?? []).filter((s) => s.isActive !== false).map((s) => ({ id: s.id, name: s.name })));
          this.leadStatesLoaded.set(true);
        },
        error: () => (this.catalogRequested.leadStates = false)
      });
    }
  }

  private loadDealCycles(): void {
    if (this.catalogRequested.dealCycles) {
      return;
    }
    this.catalogRequested.dealCycles = true;
    this.dealCycleApi.getAll().subscribe({
      next: (list) => {
        this.dealCycles.set(
          [...(list ?? [])]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((c) => ({ id: c.id, name: c.name }))
        );
        this.dealCyclesLoaded.set(true);
      },
      error: () => (this.catalogRequested.dealCycles = false)
    });
  }

  protected onLeadContactSelected(c: Contact | null): void {
    this.leadContactName.set(c ? this.contactLabel(c) : '');
  }
  protected onLeadCompanySelected(c: CompanyResponse | null): void {
    this.leadCompanyName.set(c ? this.companyLabel(c) : '');
  }

  contactSearch = (term: string, page: number): Observable<EntitySelectPage<Contact>> => {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    return this.contactoApi.searchContacts({ businessId, searchTerm: term, page, size: 10 }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages })),
      catchError(() => of({ items: [] as Contact[], totalPages: 0 }))
    );
  };
  contactLabel = (c: Contact): string => `${c.fullname}${c.lastname ? ' ' + c.lastname : ''}`.trim();
  contactSublabel = (c: Contact): string => c.mail ?? '';

  companySearch = (term: string, page: number): Observable<EntitySelectPage<CompanyResponse>> => {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    return this.empresaApi.searchCompanies({ businessId, searchTerm: term, page, size: 10 }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages })),
      catchError(() => of({ items: [] as CompanyResponse[], totalPages: 0 }))
    );
  };
  companyLabel = (c: CompanyResponse): string => c.commercialName || c.businessName || `Empresa #${c.id}`;
  companySublabel = (c: CompanyResponse): string => c.businessName ?? '';

  // ── ROUND ROBIN ──────────────────────────────────────────────────────────────
  protected readonly roundRobinIds = computed<number[]>(() =>
    this.arr('userIds').filter((n): n is number => typeof n === 'number')
  );
  protected addRoundRobin(value: string): void {
    const id = Number(value);
    if (!value.trim() || !Number.isFinite(id) || this.roundRobinIds().includes(id)) {
      return;
    }
    this.patchConfig({ userIds: [...this.roundRobinIds(), id] });
  }
  protected removeRoundRobin(id: number): void {
    this.patchConfig({ userIds: this.roundRobinIds().filter((x) => x !== id) });
  }

  // ── ROUTING rules ────────────────────────────────────────────────────────────
  protected readonly routingRules = computed<RoutingRule[]>(() => {
    const raw = this.arr('rules');
    if (!raw.length) {
      return [{ property: '', operator: 'EQUALS', ownerUserId: null, ...EMPTY_VALUE_PARTS }];
    }
    return raw.map((item) => {
      const r = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
      const owner = Number(r['ownerUserId']);
      return {
        property: String(r['property'] ?? ''),
        operator: String(r['operator'] ?? 'EQUALS'),
        ownerUserId: Number.isFinite(owner) && r['ownerUserId'] != null ? owner : null,
        ...readConditionValueParts(r)
      };
    });
  });
  protected addRule(): void {
    this.patchConfig({ rules: [...this.routingRules(), { property: '', operator: 'EQUALS', ownerUserId: null, ...EMPTY_VALUE_PARTS }] });
  }
  protected updateRule(index: number, patch: Record<string, unknown>): void {
    this.patchConfig({ rules: this.routingRules().map((r, i) => (i === index ? { ...r, ...patch } : r)) });
  }
  protected removeRule(index: number): void {
    this.patchConfig({ rules: this.routingRules().filter((_, i) => i !== index) });
  }

  // ── selectores ───────────────────────────────────────────────────────────────
  protected onListSelected(s: Segment | null): void {
    this.listName.set(s?.name ?? '');
  }
  listSearch = (term: string, page: number): Observable<EntitySelectPage<Segment>> => {
    return this.segmentService.getSegments(page, 10, { type: 'STATIC', searchTerm: term }).pipe(
      map((r) => ({ items: r.content, totalPages: r.totalPages })),
      catchError(() => of({ items: [] as Segment[], totalPages: 0 }))
    );
  };
  segmentLabel = (s: Segment): string => s.name;

  protected onEnrollWorkflowId(id: number | null): void {
    this.patchConfig({ targetWorkflowId: id });
  }
  protected onEnrollWorkflowSelected(w: WorkflowResponse | null): void {
    this.workflowName.set(w?.name ?? '');
    this.patchConfig({ targetWorkflowVersionId: w?.publishedVersionId ?? null });
  }
  protected onUnenrollWorkflowSelected(w: WorkflowResponse | null): void {
    this.workflowName.set(w?.name ?? '');
  }
  workflowSearch = (term: string, page: number): Observable<EntitySelectPage<WorkflowResponse>> => {
    const businessId = this.auth.getCurrentUser()?.businessId || 1;
    return this.workflowApi.getWorkflows({ businessId, page, size: 10, searchTerm: term || undefined }).pipe(
      map((res) => (Array.isArray(res) ? { items: res, totalPages: 1 } : { items: res.content, totalPages: res.totalPages })),
      catchError(() => of({ items: [] as WorkflowResponse[], totalPages: 0 }))
    );
  };
  workflowLabel = (w: WorkflowResponse): string => w.name;
}
